import { useState, useCallback } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { VenueBooking } from '../../types/venue';

export interface OwnerStats {
  totalRevenue: number;
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  occupancyRate: number;
  totalVenues: number;
  activeVenues: number;
  thisMonthRevenue: number;
  thisMonthBookings: number;
}

export interface OwnerVenue {
  id: string;
  name: string;
  venueType: string;
  surfaceType?: string;
  city: string;
  province: string;
  status: string;
  isActive: boolean;
  hourlyRate: number;
  complexId?: string | null;
  complex?: {
    id: string;
    name: string;
    city: string;
    province: string;
  } | null;
  photos: any[];
  averageRating?: number;
  stats: {
    totalBookings: number;
    totalRevenue: number;
    pendingBookings: number;
    reviewCount: number;
  };
  createdAt: string;
}

export const useOwnerDashboard = () => {
  const [stats, setStats] = useState<OwnerStats | null>(null);
  const [venues, setVenues] = useState<OwnerVenue[]>([]);
  const [recentBookings, setRecentBookings] = useState<VenueBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    const response = await apiRequest<OwnerStats>('/owner/stats');

    if (response.data) {
      setStats(response.data);
    } else {
      setError(response.error || 'Error al cargar estadísticas');
    }

    setLoading(false);
  }, []);

  const fetchVenues = useCallback(async () => {
    const response = await apiRequest<OwnerVenue[]>('/owner/venues');

    if (response.data) {
      setVenues(response.data);
    }
  }, []);

  const fetchRecentBookings = useCallback(async (limit: number = 10) => {
    const response = await apiRequest<VenueBooking[]>(
      `/owner/bookings/recent?limit=${limit}`
    );

    if (response.data) {
      setRecentBookings(response.data);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([fetchStats(), fetchVenues(), fetchRecentBookings()]);
    } catch (err) {
      setError('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  }, [fetchStats, fetchVenues, fetchRecentBookings]);

  return {
    stats,
    venues,
    recentBookings,
    loading,
    error,
    fetchStats,
    fetchVenues,
    fetchRecentBookings,
    fetchAll,
  };
};
